/* CoE Escalations — iter98.99dx Phase B
 *
 * Coach-centric list of every clinical concern this PES has raised back
 * to the doctor. Renders /api/coe/compliance/my-escalations with the
 * status filter chips (Open · Resolved · All).
 *
 * Open rows surface the case + raise date + the coach's note. Once
 * resolved, the doctor's note + resolution timestamp render below the
 * open row so the coach has the full thread on one card.
 *
 * Click a card → deep-link to /coe/compliance/cases/{case_id} for the
 * full conversation + ability to raise a follow-up if needed.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    ShieldWarning, ShieldCheck, CheckCircle, ArrowRight, ArrowsClockwise, Stethoscope,
    ChatCircle, PaperPlaneTilt, X, ArrowSquareOut, Headset, ListBullets,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AdminKpiTile, CommandHeader } from "@/components/admin/AdminKpiTile";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";


const STATUS_TABS = [
    { id: "open",     label: "Open" },
    { id: "resolved", label: "Resolved" },
    { id: "all",      label: "All" },
];


function relTime(iso) {
    if (!iso) return "—";
    try {
        const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
        if (days === 0) return "today";
        if (days === 1) return "1 day ago";
        if (days < 30) return `${days} days ago`;
        const m = Math.floor(days / 30);
        return m === 1 ? "1 month ago" : `${m} months ago`;
    } catch { return "—"; }
}


export default function CoeEscalations() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("open");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/coe/compliance/my-escalations");
            setRows(r.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const counts = useMemo(() => ({
        open:     rows.filter((r) => r.status === "open").length,
        resolved: rows.filter((r) => r.status === "resolved").length,
        all:      rows.length,
    }), [rows]);

    const visible = useMemo(() => {
        if (tab === "all") return rows;
        return rows.filter((r) => r.status === tab);
    }, [rows, tab]);

    return (
        <div className="space-y-6" data-testid="coe-escalations-page">
            <CommandHeader
                overline="Patient Engagement · Escalations"
                title="Escalations"
                subtitle="Clinical concerns you've raised back to the doctor. Open items await the doctor's review; resolved items carry the doctor's response."
                testid="coe-escalations-header"
                right={
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-sm hover:bg-secondary/40 transition"
                        data-testid="coe-escalations-refresh"
                    >
                        <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                }
            />

            {/* iter101.40 — 3-tile KPI strip. Kept lean (Open · Resolved
                · Total) — same Admin Command Center grammar just fewer
                columns because escalations only have two meaningful
                slices. Clicking a tile swaps the active tab. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="coe-escalations-kpi-strip">
                <AdminKpiTile label="Open"          value={counts.open ?? 0}     tone="rose"    Icon={ShieldWarning} active={tab === "open"}     onClick={() => setTab("open")}     testid="coe-escalations-kpi-open" />
                <AdminKpiTile label="Resolved"      value={counts.resolved ?? 0} tone="emerald" Icon={ShieldCheck}   active={tab === "resolved"} onClick={() => setTab("resolved")} testid="coe-escalations-kpi-resolved" />
                <AdminKpiTile label="Total raised"  value={counts.all ?? 0}      tone="primary" Icon={ListBullets}   active={tab === "all"}      onClick={() => setTab("all")}      testid="coe-escalations-kpi-all" />
            </div>

            <AdminSegmentedTabs
                tabs={STATUS_TABS.map((t) => ({
                    id: t.id,
                    label: t.label,
                    Icon: t.id === "open" ? ShieldWarning : t.id === "resolved" ? ShieldCheck : ListBullets,
                    count: counts[t.id] ?? 0,
                }))}
                active={tab}
                onChange={setTab}
                testid="coe-escalations-tabs"
                tabTestidBase="coe-escalations-tab"
                showZero
            />

            {/* Body */}
            {loading ? (
                <div className="p-10 text-center text-sm text-muted-foreground">Loading escalations…</div>
            ) : visible.length === 0 ? (
                <EmptyState tab={tab} />
            ) : (
                <div className="space-y-3" data-testid="coe-escalations-list">
                    {visible.map((e) => (
                        <EscalationCard
                            key={e.id}
                            esc={e}
                            onUpdate={(updated) => setRows((arr) => arr.map((r) => (r.id === updated.id ? updated : r)))}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}


function EscalationCard({ esc, onUpdate }) {
    const isOpen = esc.status === "open";
    const [replyOpen, setReplyOpen] = useState(false);
    const comments = esc.comments || [];

    return (
        <div
            className={`rounded-2xl border bg-card overflow-hidden transition-shadow hover:shadow-md ${
                isOpen ? "border-rose-200" : "border-border"
            }`}
            data-testid={`coe-escalation-card-${esc.id}`}
        >
            <div className="px-5 py-4">
                <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <StatusPill status={esc.status} />
                            <span className="font-mono text-[11px] text-muted-foreground">{esc.case_code}</span>
                            <span className="text-[11px] text-muted-foreground">·</span>
                            <span className="text-[11px] text-muted-foreground">{relTime(esc.created_at)}</span>
                        </div>
                        <div className="mt-2 font-display text-base font-bold leading-tight">
                            {esc.patient_name || "Unnamed patient"}
                        </div>
                    </div>
                    <Link
                        to={`/coe/compliance/cases/${esc.case_id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11.5px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition"
                        data-testid={`coe-escalation-open-case-${esc.id}`}
                    >
                        <ArrowSquareOut size={11} weight="bold" /> Case
                    </Link>
                </div>
                <p className="mt-2.5 text-[13px] text-foreground/85 leading-relaxed">
                    <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-rose-600 mr-1.5">Your note:</span>
                    {esc.note || <span className="italic text-muted-foreground">(no note)</span>}
                </p>

                {/* iter98.99ee — Multi-turn comment thread */}
                {comments.length > 0 && (
                    <ul className="mt-4 space-y-2" data-testid={`coe-escalation-thread-${esc.id}`}>
                        {comments.map((c) => <CommentBubble key={c.id} comment={c} />)}
                    </ul>
                )}
            </div>

            {/* Resolved tail */}
            {!isOpen && (
                <div className="px-5 py-3 border-t border-border bg-emerald-50/40">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Stethoscope size={13} weight="duotone" className="text-emerald-700" />
                        <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-emerald-700">Doctor&apos;s resolution</span>
                        {esc.resolved_at && (
                            <span className="text-[10.5px] text-muted-foreground">· {relTime(esc.resolved_at)}</span>
                        )}
                    </div>
                    <p className="text-[13px] text-foreground/85 leading-relaxed">
                        {esc.resolved_note || <span className="italic text-muted-foreground">Doctor marked this resolved without a note.</span>}
                    </p>
                </div>
            )}

            {/* iter98.99ee — Coach reply composer (open OR resolved — the
                conversation can continue post-resolution if needed). */}
            <div className="px-5 py-3 border-t border-border bg-secondary/20 flex items-center justify-end gap-2" data-testid={`coe-escalation-actions-${esc.id}`}>
                {!replyOpen ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setReplyOpen(true)}
                        data-testid={`coe-escalation-reply-toggle-${esc.id}`}
                    >
                        <ChatCircle size={12} weight="fill" className="mr-1.5" /> Reply to doctor
                    </Button>
                ) : (
                    <CoachReplyComposer
                        escalationId={esc.id}
                        onCancel={() => setReplyOpen(false)}
                        onSaved={(updated) => {
                            setReplyOpen(false);
                            onUpdate?.(updated);
                            toast.success("Reply sent");
                        }}
                    />
                )}
            </div>
        </div>
    );
}


/* iter98.99ee — Comment bubble. Doctor replies hug the left (received from
   the doctor's side); coach's own replies hug the right (sent). */
function CommentBubble({ comment }) {
    const isDoctor = comment.author_role === "doctor";
    const isSelf = comment.author_role === "staff";
    const author = isDoctor
        ? "Doctor"
        : (isSelf ? "You" : (comment.author_name || "—"));
    return (
        <li
            className={`flex ${isSelf ? "justify-end" : "justify-start"}`}
            data-testid={`coe-escalation-comment-${comment.id}`}
        >
            <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
                    isSelf
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-white border border-border rounded-bl-md"
                }`}
            >
                <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1 ${isSelf ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {isDoctor && <Stethoscope size={10} weight="fill" />}
                    {!isDoctor && !isSelf && <Headset size={10} weight="fill" />}
                    {author} · {relTime(comment.created_at)}
                </div>
                <div className="whitespace-pre-wrap">{comment.text}</div>
            </div>
        </li>
    );
}


function CoachReplyComposer({ escalationId, onCancel, onSaved }) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);

    const submit = async () => {
        const t = text.trim();
        if (!t) return;
        setSending(true);
        try {
            const r = await api.post(`/coe/compliance/escalations/${escalationId}/comment`, { text: t });
            onSaved?.(r.data);
            setText("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send reply");
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            submit();
        }
    };

    return (
        <div className="w-full flex flex-col gap-2" data-testid={`coe-escalation-reply-composer-${escalationId}`}>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                rows={3}
                placeholder="Reply to the doctor (Cmd/Ctrl+Enter to send)…"
                className="w-full resize-none text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 transition"
                data-testid={`coe-escalation-reply-input-${escalationId}`}
                autoFocus
            />
            <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onCancel} type="button">
                    <X size={12} className="mr-1" /> Cancel
                </Button>
                <Button
                    type="button"
                    size="sm"
                    onClick={submit}
                    disabled={sending || !text.trim()}
                    className="bg-primary hover:bg-primary/90"
                    data-testid={`coe-escalation-reply-send-${escalationId}`}
                >
                    <PaperPlaneTilt size={12} weight="fill" className="mr-1.5" />
                    {sending ? "Sending…" : "Send"}
                </Button>
            </div>
        </div>
    );
}


function StatusPill({ status }) {
    if (status === "open") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700">
                <ShieldWarning size={10} weight="fill" /> Open
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700">
            <ShieldCheck size={10} weight="fill" /> Resolved
        </span>
    );
}


function EmptyState({ tab }) {
    const text = tab === "open"
        ? "No open escalations right now — everything is back with you."
        : tab === "resolved"
            ? "Nothing's been resolved yet."
            : "You haven't raised any escalations yet. Open any compliance case and use \"Escalate to doctor\" when a clinical concern needs review.";
    return (
        <div className="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 grid place-items-center mb-3">
                <CheckCircle size={24} className="text-emerald-600" weight="duotone" />
            </div>
            <div className="font-display text-base font-semibold">All clear</div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto leading-relaxed">{text}</p>
        </div>
    );
}
